import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API_URL from "../../config/apiConfig";
import FloorPlanCanvas from "../../components/FloorPlanCanvas";
import { getPublicFloorPlan } from "../../api/reservations";

const initialDate = new Date().toISOString().slice(0, 10);
const preferenceOptions = ["WINDOW", "OUTDOOR", "INDOOR", "QUIET", "VIP", "FAMILY", "ACCESSIBLE", "NON_SMOKING"];

export default function ReservationPage() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ reservationDate: initialDate, startTime: "19:00", partySize: 2, preferences: [], specialRequest: "" });
  const [availability, setAvailability] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [floorData, setFloorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const requestBody = useMemo(() => ({
    restaurantId,
    reservationDate: form.reservationDate,
    startTime: form.startTime,
    partySize: Number(form.partySize),
    tableId: selectedTableId,
    preferences: form.preferences,
  }), [restaurantId, form, selectedTableId]);

  const checkAvailability = async () => {
    setLoading(true); setError(""); setAvailability(null); setSelectedTableId(null);
    try {
      const response = await fetch(`${API_URL}/api/reservations/availability`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(requestBody),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error?.message || "Unable to load availability.");
      setAvailability(data);
      if (data.exactTableSelectionEnabled) {
        try { setFloorData(await getPublicFloorPlan(restaurantId)); } catch { setFloorData(null); }
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally { setLoading(false); }
  };

  const confirmReservation = async () => {
    const token = localStorage.getItem("customerToken");
    if (!token) { setError("Please sign in as a customer before confirming a reservation."); return; }
    if (!availability) { setError("Check availability before confirming."); return; }
    setLoading(true); setError("");
    try {
      const response = await fetch(`${API_URL}/api/reservations/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Idempotency-Key": crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        },
        body: JSON.stringify({ ...requestBody, specialRequest: form.specialRequest }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error?.message || "Reservation could not be created.");
      navigate("/p/reservation-result", { state: { reservation: data.reservation } });
    } catch (requestError) {
      setError(requestError.message);
      if (requestError.message.includes("no longer available")) checkAvailability();
    } finally { setLoading(false); }
  };

  return (
    <main className="min-h-screen bg-gray-50 p-4 md:p-8" dir="rtl">
      <section className="mx-auto max-w-2xl rounded-2xl bg-white p-6 shadow-sm">
        <h1 className="mb-6 text-2xl font-bold text-gray-900">رزرو میز</h1>
        <div className="grid gap-4 md:grid-cols-3">
          <label className="text-sm font-medium">تاریخ<input className="mt-1 w-full rounded-lg border p-3" type="date" min={initialDate} value={form.reservationDate} onChange={(e) => setForm({ ...form, reservationDate: e.target.value })} /></label>
          <label className="text-sm font-medium">ساعت<input className="mt-1 w-full rounded-lg border p-3" type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} /></label>
          <label className="text-sm font-medium">تعداد نفرات<input className="mt-1 w-full rounded-lg border p-3" type="number" min="1" max="100" value={form.partySize} onChange={(e) => setForm({ ...form, partySize: e.target.value })} /></label>
        </div>
        <button className="mt-5 w-full rounded-xl bg-primary py-3 font-medium text-white disabled:opacity-50" disabled={loading} onClick={checkAvailability}>بررسی میزهای خالی</button>
        {availability && <>
          <p className="mt-6 text-sm text-gray-600">زمان رزرو تا {availability.endTime} است.{availability.exactTableSelectionEnabled ? " یک میز را انتخاب کنید:" : " میز مناسب به‌صورت خودکار تعیین می‌شود."}</p>
          {availability.exactTableSelectionEnabled && floorData?.floorPlan && <div className="mt-4"><FloorPlanCanvas plan={floorData.floorPlan} elements={floorData.elements} tables={floorData.tables} states={Object.fromEntries(floorData.tables.map((table) => [String(table._id), availability.tables.some((available) => String(available.id) === String(table._id)) ? "AVAILABLE" : "BLOCKED"]))} selectedId={floorData.elements.find((element) => String(element.tableId) === String(selectedTableId))?._id} onSelect={(elementId) => { const element = floorData.elements.find((item) => item._id === elementId); if (availability.tables.some((table) => String(table.id) === String(element?.tableId))) setSelectedTableId(element.tableId); }} zoom={0.6} /></div>}
          {availability.exactTableSelectionEnabled && <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {availability.tables.map((table) => <button key={table.id} onClick={() => setSelectedTableId(table.id)} className={`rounded-xl border p-4 text-right ${selectedTableId === table.id ? "border-primary bg-orange-50" : "border-gray-200"}`}>
              <strong>{table.name}</strong><span className="mr-2 text-sm text-gray-600">ظرفیت {table.capacity}</span><p className="mt-1 text-xs text-gray-500">{table.section} · {table.attributes.join("، ")}</p>
            </button>)}
          </div>}
          {availability.bookingMode === "PREFERENCE_ONLY" && <fieldset className="mt-5"><legend className="mb-2 text-sm font-medium">ترجیحات میز</legend><div className="flex flex-wrap gap-3">{preferenceOptions.map((preference) => <label key={preference} className="rounded-lg border px-3 py-2 text-xs"><input type="checkbox" checked={form.preferences.includes(preference)} onChange={(e) => setForm({ ...form, preferences: e.target.checked ? [...form.preferences, preference] : form.preferences.filter((item) => item !== preference) })} /> {preference}</label>)}</div></fieldset>}
          <label className="mt-5 block text-sm font-medium">درخواست ویژه<textarea className="mt-1 w-full rounded-lg border p-3" rows="3" value={form.specialRequest} onChange={(e) => setForm({ ...form, specialRequest: e.target.value })} /></label>
          <button className="mt-5 w-full rounded-xl bg-primary py-3 font-medium text-white disabled:opacity-50" disabled={loading || (availability.exactTableSelectionEnabled && !selectedTableId)} onClick={confirmReservation}>تأیید رزرو</button>
        </>}
        {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      </section>
    </main>
  );
}
